// Admin-managed banner (Firestore `banners` collection, see
// gamzone/public/main/admin.html). Same collection the reference games
// (2026, Coin Exchanger) read — shared project, public read, admin-only
// write — so nothing extra needs deploying for this game to use it.
//
// Two types: "panel" (AdSlot.tsx's footer banner) and "interstitial" (the
// idle-pause placement, see MatchScreen.tsx).

import { Capacitor } from "@capacitor/core";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../auth/firebase.ts";
import { GAME_ID } from "../../gameId.ts";

/**
 * These admin banners are web-only, any device — "mobile" here means the
 * native app (Capacitor), not a phone-width browser. There's no separate
 * mobile-web layout to protect: game.css's responsive layout (.bo-card
 * etc.) is already identical for phone and desktop browsers, so the
 * footer's fixed-fit height (shell.css .bo-controls .gz-ad-slot--banner)
 * applies the same everywhere web runs. Only the native app keeps its
 * separate AdMob path untouched.
 */
export function isWebRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return !Capacitor.isNativePlatform();
}

export interface AdminBanner {
  imageUrl: string;
  linkUrl?: string;
  type?: string;
  gameId?: string | null;
  excludeGameIds?: string[];
  order?: number;
  active?: boolean;
}

let activeBannersPromise: Promise<AdminBanner[]> | null = null;

function fetchActiveBanners(): Promise<AdminBanner[]> {
  if (activeBannersPromise) return activeBannersPromise;
  activeBannersPromise = (async () => {
    if (!db) return [];
    try {
      const snap = await getDocs(query(collection(db, "banners"), where("active", "==", true)));
      return snap.docs.map((d) => d.data() as AdminBanner);
    } catch (error) {
      console.warn("[ads] active banners fetch failed", error);
      return [];
    }
  })();
  return activeBannersPromise;
}

function pickBanner(banners: AdminBanner[], type: string): AdminBanner | null {
  const candidates = banners
    .filter((b) => (b.type ?? "panel") === type)
    .filter((b) => !b.gameId || b.gameId === GAME_ID)
    .filter((b) => !(b.excludeGameIds ?? []).includes(GAME_ID))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return candidates[0] ?? null;
}

/** Resolves to the active admin-uploaded interstitial banner for this game, or null. */
export async function getInterstitialBanner(): Promise<AdminBanner | null> {
  const banners = await fetchActiveBanners();
  return pickBanner(banners, "interstitial");
}

/** Resolves to the active admin-uploaded panel (footer slot) banner for this game, or null. */
export async function getPanelBanner(): Promise<AdminBanner | null> {
  const banners = await fetchActiveBanners();
  return pickBanner(banners, "panel");
}
